/**
 * GA4 send layer — dataLayer only.
 *
 * GTM is the single tag runtime on this site; index.html no longer loads gtag.js.
 * Everything here pushes to window.dataLayer and GTM forwards it to GA4, so the
 * measurement ID lives in the container, not in this repo. Degrades to a no-op
 * when dataLayer is absent (tests, SSR).
 *
 * Why not gtag(): with the GTM container loaded, gtag('event', ...) calls were
 * consumed by GTM and never sent — proven by a three-way control (container on →
 * zero requests; container off → request sent). Do not reintroduce gtag.js.
 */
type DataLayer = Record<string, unknown>[];

function getDataLayer(): DataLayer | undefined {
  if (typeof window === 'undefined') return undefined;
  const w = window as unknown as { dataLayer?: DataLayer };
  if (!Array.isArray(w.dataLayer)) return undefined;
  return w.dataLayer;
}

/**
 * Set values that later events should carry (e.g. user_id). Pushed as a bare
 * dataLayer entry — no `event` key — so it updates GTM variables without firing
 * a trigger.
 */
export function gaSet(params: Record<string, unknown>): void {
  getDataLayer()?.push({ ...params });
}

/**
 * GA4 User-ID. Call with the business user id on login/registration, null on
 * logout. The container reads `user_id` off the dataLayer and puts it on the tag.
 * https://developers.google.com/analytics/devguides/collection/ga4/user-id
 */
export function setGaUserId(userId: string | null): void {
  gaSet({ user_id: userId });
}

/**
 * Every business param any event can carry. Each push resets ALL of them to
 * undefined before applying this event's own values.
 *
 * This is not defensive noise — dataLayer values persist across pushes, and GTM's
 * Data Layer Variables read whatever is current when a tag fires. Without the
 * reset, params bleed forward: pricing_view arrived in GA4 carrying `method`,
 * `category`, `action`, `selected_count`, `Industry` and `industry_count` left
 * over from earlier events (observed on the wire, not theory). Add new params
 * here as well as at the call site, or they will leak into later events.
 */
const PARAM_KEYS = [
  'entry_flow', 'method',
  'category', 'action', 'selected_count',
  'Industry', 'industry_source', 'industry_count',
  // Shopify billing funnel (plan_selected -> shopify_install_redirected ->
  // shopify_oauth_completed -> billing_confirmation_view -> trial_started).
  // These were emitted by their call sites but never listed here, so they bled
  // forward into later events the same way `method`/`category` used to.
  'plan', 'billing_cycle', 'has_shopify_installation',
  'placement', 'destination', 'platform', 'is_trial',
] as const;

/**
 * Fire a product event. `event` is the key GTM's custom-event trigger matches;
 * the remaining keys are the business params, spread flat so each one can be
 * mapped 1:1 in the container. Param names are load-bearing — they are registered
 * as GA4 custom dimensions and cannot be renamed.
 */
export function gaEvent(eventName: string, params?: Record<string, unknown>): void {
  const reset: Record<string, unknown> = {};
  for (const k of PARAM_KEYS) reset[k] = undefined;
  getDataLayer()?.push({ event: eventName, ...reset, ...(params ?? {}) });
}

/**
 * GA4 client_id, read straight from the `_ga` cookie (format `GA1.1.<cid>`).
 *
 * Previously this used gtag('get', ...). With gtag.js gone there is no such API —
 * GTM exposes no equivalent to the page — so we parse the cookie GA4 itself
 * writes. Same value, no tag runtime needed. Resolves null before the cookie
 * exists (first hit of a brand-new visitor) or when storage is blocked.
 *
 * The async signature is kept so callers (trackEvent) are unchanged.
 */
export function getGaClientId(): Promise<string | null> {
  return Promise.resolve(readGaCookieClientId());
}

function readGaCookieClientId(): string | null {
  try {
    const raw = document.cookie
      .split('; ')
      .find(row => row.startsWith('_ga='));
    if (!raw) return null;
    // `_ga=GA1.1.1234567890.1700000000` -> `1234567890.1700000000`
    const parts = decodeURIComponent(raw.slice('_ga='.length)).split('.');
    return parts.length >= 4 ? `${parts[2]}.${parts[3]}` : null;
  } catch {
    return null;
  }
}
